import React, {ReactNode} from 'react';

import DataConnector from 'connect/DataConnector';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import ActionBarSkeleton from 'insights/analytics-dashboards/details/ActionBarSkeleton';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {
  isLocalSQLQueryUnsaved,
  resetLocalSQLQueryOverride,
} from 'insights/analytics-queries/data/LocalSQLQueryOverride';
import {AnalyticsQuerySpecRequest} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import {
  runGraphQLAnalyticsQuery,
  runSubQuery,
} from 'insights/analytics-queries/run-query/RunAnalyticsQueryActions';
import AnalyticsTabSkeleton from 'insights/AnalyticsTabSkeleton';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {AnalyticsParameterConfigSpecRequest} from 'insights/parameters/RESTParameterTypes';
import {RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {makeReactContext} from 'lib/makeReactContext';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import AnalyticsBlockEditorContent from './AnalyticsBlockEditorContent';
import AnalyticsBlockEditorHeader from './AnalyticsBlockEditorHeader';
import {deleteSQLTemplateForm, updateSQLTemplateForm} from './BlockEditorReduxActions';
import {BlockEditorUIState, DEFAULT_BLOCK_EDITOR_UI_STATE} from './reduceBlockEditorUIs';
import runConfirmOpenDashboardModal from './runConfirmOpenDashboardModal';
import SaveAndRunButton from './SaveAndRunButton';
import {isSQLTemplateFormStateValid} from './sql-template-form/interpolation/makeSQLInterpolationInput';
import {SQLTemplateFormState} from './sql-template-form/SQLTemplateFormTypes';
import useSaveAndRunQuery from './useSaveAndRunQuery';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type AnalyticsQuery = AnalyticsDashboard['analyticsQueries'][number];

interface UnconnectedActions {
  deleteAnalyticsQuery: (analyticsQueryId: string) => void;
  openDashboardEditor: () => void;
  updateAnalyticsQuery: (
    analyticsQueryId: string,
    data: AnalyticsQuerySpecRequest,
    additionalParameterConfigs?: Array<AnalyticsParameterConfigSpecRequest>
  ) => Promise<void>;
}

interface ConnectedActions {
  runGraphQLAnalyticsQuery: typeof runGraphQLAnalyticsQuery;
  runSubQuery: typeof runSubQuery;
  setSQLTemplateFormState: (newSQLTemplateFormState: SQLTemplateFormState) => void;
  switchToSQLEditor: () => void;
}

export interface ConnectedAnalyticsBlockEditorProps {
  actions: ConnectedActions;
  blockEditorUIState: BlockEditorUIState;
}

export interface UnconnectedAnalyticsBlockEditorProps {
  actions: UnconnectedActions;
  analyticsDashboardAttrs: Pick<AnalyticsDashboard, 'id' | 'createdAt' | 'creator' | 'modifiedAt' | 'name'>;
  analyticsQuery: AnalyticsQuery;
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>;
  parameterList: ReactNode;
  parameterValues: Array<AnalyticsParameterValue>;
}

export type AnalyticsBlockEditorProps = UnconnectedAnalyticsBlockEditorProps &
  ConnectedAnalyticsBlockEditorProps;

export class BlockEditorConnector extends DataConnector<
  UnconnectedAnalyticsBlockEditorProps,
  ConnectedAnalyticsBlockEditorProps
> {
  enhanceProps(): ConnectedAnalyticsBlockEditorProps {
    const blockEditorUIState =
      this.reduxState().ui.insights.blockEditorUIs[this.props.analyticsQuery.id] ??
      DEFAULT_BLOCK_EDITOR_UI_STATE;

    return {
      actions: {
        ...this.props.actions,
        runGraphQLAnalyticsQuery,
        runSubQuery,
        setSQLTemplateFormState: (newSQLTemplateFormState: SQLTemplateFormState) => {
          this.dispatch(updateSQLTemplateForm(this.props.analyticsQuery.id, newSQLTemplateFormState));
        },
        switchToSQLEditor: () => {
          this.dispatch(deleteSQLTemplateForm(this.props.analyticsQuery.id));
        },
      },
      blockEditorUIState,
    };
  }
}

interface DraftQueryContext {
  draftSQLQuery: string;
  setDraftSQLQuery: (newDraftQuery: string) => void;
}

export const DraftQueryContext = makeReactContext<DraftQueryContext>(
  () => ({
    draftSQLQuery: '',
    setDraftSQLQuery: (newDraftQuery: string) => {},
  }),
  'DraftQueryContext'
);

export function useDraftQueryContext(): DraftQueryContext {
  return DraftQueryContext.useContext();
}

export function getSaveAndRunDisableMessage(
  blockEditorUI: BlockEditorUIState,
  runQueryState: RunQueryState
): string | null {
  if (runQueryState.status === AsyncStatus.LOADING) {
    return 'Query is already running';
  } else if (
    blockEditorUI.sqlTemplateFormState != null &&
    !isSQLTemplateFormStateValid(blockEditorUI.sqlTemplateFormState)
  ) {
    return 'The form has errors that must be fixed before the query can be run';
  }

  return null;
}

/**
 * AnalyticsBlockEditor is one of the 2 views of AnalyticsDashboardDetails. It renders an individual block
 * from the dashboard (with block header) and allows the user to edit it (SQL query, visualization settings,
 * etc.). This component is distinct from AnalyticsBlock: this renders a whole tab, that only renders the
 * visualization for a pane within the dashboard editor.
 */
export function AnalyticsBlockEditor(props: AnalyticsBlockEditorProps): JSX.Element {
  const {
    draftSQLQuery,
    setDraftSQLQuery,
    runQueryState,
    saveAndRunQuery,
    isResultsFromSubQuery,
    sqlSubQuery,
    setSQLSubQuery,
  } = useSaveAndRunQuery(props);

  async function conditionallyOpenDashboardEditor(): Promise<void> {
    if (isLocalSQLQueryUnsaved(props.analyticsQuery)) {
      const shouldDiscardAndOpen = await runConfirmOpenDashboardModal(props.blockEditorUIState);
      if (!shouldDiscardAndOpen) {
        return;
      }
      resetLocalSQLQueryOverride(props.analyticsQuery.id);
    }
    props.actions.openDashboardEditor();
  }

  const actionBar = (
    <ActionBarSkeleton
      leftButtons={
        <SaveAndRunButton
          isDisabledMessage={getSaveAndRunDisableMessage(props.blockEditorUIState, runQueryState)}
          isSubQuery={sqlSubQuery != null}
          onSaveAndRun={saveAndRunQuery}
        />
      }
      parameterList={props.parameterList}
    />
  );

  const header = (
    <>
      <AnalyticsBlockEditorHeader
        actions={{...props.actions, openDashboardEditor: conditionallyOpenDashboardEditor}}
        dashboardName={props.analyticsDashboardAttrs.name}
        analyticsQuery={props.analyticsQuery}
      />
      {actionBar}
    </>
  );

  const content = (
    <AnalyticsBlockEditorContent
      actions={{...props.actions, setDraftSQLQuery, setSQLSubQuery, saveAndRunQuery}}
      analyticsDashboardAttrs={props.analyticsDashboardAttrs}
      analyticsQuery={props.analyticsQuery}
      blockEditorUIState={props.blockEditorUIState}
      draftSQLQuery={draftSQLQuery}
      isDisplayingSubQueryResults={isResultsFromSubQuery}
      parameterConfigs={props.parameterConfigs}
      parameterList={props.parameterList}
      parameterValues={props.parameterValues}
      runQueryState={runQueryState}
      sqlSubQuery={sqlSubQuery}
    />
  );

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
    <DraftQueryContext.Provider value={{draftSQLQuery, setDraftSQLQuery}}>
      <AnalyticsTabSkeleton
        header={header}
        dataTestAttrs={{component: 'AnalyticsBlockEditor', element: 'root'}}
      >
        {content}
      </AnalyticsTabSkeleton>
    </DraftQueryContext.Provider>
  );
}

export default BlockEditorConnector.connect(AnalyticsBlockEditor);
